import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, TemplateRef, ViewContainerRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject, Subscription, forkJoin } from 'rxjs';
import { ModuleTypes } from 'src/app/modules/settings/enums/moduleTypes';
import { UserGroupService } from 'src/app/modules/settings/services/user-group-service';
import { UserGroupViewModel } from 'src/app/modules/settings/viewModels/userGroups/userGroupViewModel';
import { EmployeeRole } from 'src/app/modules/shared/employeeRole';
import { DepartmentVisibilities } from 'src/app/modules/shared/enums/departmentVisibility.enum';
import { EmployeeRoleTypes } from 'src/app/modules/shared/enums/employees/EmployeeRoleTypes';
import { FilterTypes } from 'src/app/modules/shared/enums/filterTypes';
import { FixedFilters } from 'src/app/modules/shared/enums/fixedFilters.enum';
import { OperationTypes } from 'src/app/modules/shared/enums/operationTypes';
import { Account } from 'src/app/modules/shared/models/account';
import { Employee } from 'src/app/modules/shared/models/employee';
import { FilterViewModel } from 'src/app/modules/shared/models/filter/filterViewModel';
import { WtValidators } from 'src/app/modules/shared/reactiveValidators/wtValidators';
import { AlertService } from 'src/app/modules/shared/services/alert.service';
import { AuthenticationService } from 'src/app/modules/shared/services/authentication.service';
import { ConfirmationService } from 'src/app/modules/shared/services/confirmation.service';
import { EmployeeSettingsService } from 'src/app/modules/shared/services/employee-settings.service';
import { AllowedFiltersService } from 'src/app/modules/shared/services/allowed-filters.service';
import { LocalisationService } from 'src/app/modules/shared/services/localisation.service';
import { EmployeeUtil } from 'src/app/modules/shared/utilities/employee.utilities';
import { EmployeeDetailsViewModel } from 'src/app/modules/shared/viewModels/employeeDetailsViewModel';
import { PermissionViewModel } from 'src/app/modules/shared/viewModels/permissionViewModel';
import { ControlEmployeePermissionsComponent } from '../control-employee-permissions/control-employee-permissions.component';
import { PlanningEmployeePermissionsComponent } from '../planning-employee-permissions/planning-employee-permissions.component';
import { RiskEmployeePermissionsComponent } from '../risk-employee-permissions/risk-employee-permissions.component';
import { RunsheetEmployeePermissionsComponent } from '../runsheet-employee-permissions/runsheet-employee-permissions.component';
import { SustainabilityEmployeePermissionsComponent } from '../sustainability-employee-permissions/sustainability-employee-permissions.component';
import { FixedZoneFiltersService } from 'src/app/modules/shared/services/fixed-zone-filters.service';
import { TranslateService } from '@ngx-translate/core';
import { T } from 'src/assets/i18n/translation-keys';
import { ObjectTypes } from 'src/app/modules/shared/enums/objectTypes';
import { EmployeeIdpConfirmViewModel } from 'src/app/modules/settings/viewModels/employeeIdpConfirmViewModel';
import { IdpService } from 'src/app/modules/settings/services/idp-service';
import { AuthenticationManager } from 'src/app/modules/shared/managers/authenticationManager'

enum ReadWriteTypes {
  read = 'read',
  write = 'write',
}

const FORM_FIELDS = ['firstName', 'surname', 'username'] as const;
type EmployeeFormModel = Pick<EmployeeDetailsViewModel, typeof FORM_FIELDS[number]>;
type EmployeeFormControls = {
  [K in keyof EmployeeFormModel]: FormControl<EmployeeFormModel[K]>;
};

@Component({
    selector: 'app-manage-employee',
    templateUrl: './manage-employee.component.html',
    styleUrls: ['./manage-employee.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ManageEmployeeComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('planningComponent') planningComponent: PlanningEmployeePermissionsComponent;
  @ViewChild('riskComponent') riskComponent: RiskEmployeePermissionsComponent;
  @ViewChild('controlComponent') controlComponent: ControlEmployeePermissionsComponent;
  @ViewChild('runsheetsComponent') runsheetsComponent: RunsheetEmployeePermissionsComponent;
  @ViewChild('sustainabilityComponent') sustainabilityComponent: SustainabilityEmployeePermissionsComponent;

  private employeeUserGroups: UserGroupViewModel[] = [];
  public isHubAccount = false;
  public isAddMode = true;
  public filters: FilterViewModel[] = [];
  public selectedDepartmentId = 0;
  public selectedZoneId = 0;
  public isInPrimaryRole = false;
  public isAccountAdmin = false;
  public isExternal = false;
  public accountFixedFilter: FixedFilters;
  public form: FormGroup<EmployeeFormControls>;
  public employeeDetailsViewModel: EmployeeDetailsViewModel = new EmployeeDetailsViewModel();
  public saveButtonLoader = false;
  public promoteExternalUser = false;

  // These will be for subscriptions for them out of the modal
  public onSave = new Subject<EmployeeDetailsViewModel>();
  public onDelete = new Subject<EmployeeDetailsViewModel>();

  public readonly filterTypes = FilterTypes;
  public readonly departmentVisibilites = DepartmentVisibilities;
  public readonly employeeRoleTypes = EmployeeRoleTypes;
  public readonly moduleTypes = ModuleTypes;
  public readonly readWriteTypes = ReadWriteTypes;
  public readonly fixedFilters = FixedFilters;
  public account: Account;
  private employee: Employee;
  private subscriptions = new Subscription();
  public selectedModuleTab: string = 'planning';
  public localisedEmployee: string;
  public localisedDepartment: string;
  public moduleTabsIds: string[] = ['planning', 'control', 'runsheets'];
  public readonly T = T;

  public isConfirmUserIdpVisible = false;
  public employeeConfirmViewModel: EmployeeIdpConfirmViewModel;
  public externalIsAvailable: boolean;

  constructor(
    private readonly bsModalRef: BsModalRef,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly authenticationService: AuthenticationService,
    private readonly employeeSettingsService: EmployeeSettingsService,
    private readonly alertService: AlertService,
    private readonly localisationService: LocalisationService,
    private readonly confirmationService: ConfirmationService,
    private readonly allowedFiltersService: AllowedFiltersService,
    private readonly fb: FormBuilder,
    private readonly userGroupService: UserGroupService,
    private readonly fixedZoneFiltersService: FixedZoneFiltersService,
    private readonly translateService: TranslateService,
    private readonly wtValidators: WtValidators,
    private readonly idpService: IdpService,
    private readonly authenticationManager: AuthenticationManager
  ) {}

  ngOnInit(): void {
    this.employee = this.authenticationService.getCurrentEmployee();
    this.account = this.authenticationService.getCurrentAccount();
    this.localisedEmployee = this.localisationService.localiseObjectType(ObjectTypes.Employee);
    this.localisedDepartment = this.localisationService.localiseObjectType(ObjectTypes.Department);

    this.selectVisibleTabsed();
    this.initForm();

    if (this.promoteExternalUser) {
      this.isAddMode = false;
      this.employeeDetailsViewModel.isExternal = false;
      this.employeeDetailsViewModel.employeeRole = [];
      this.employeeDetailsViewModel.permissions = [];
      this.changeDetectorRef.detectChanges();
    } else {
      if (!this.employeeDetailsViewModel || !this.employeeDetailsViewModel.id) {
        this.employeeDetailsViewModel = new EmployeeDetailsViewModel();
        this.employeeDetailsViewModel.employeeRole = [];
        this.employeeDetailsViewModel.permissions = [];
        this.employeeDetailsViewModel.isExternal = this.isExternal;
        this.changeDetectorRef.detectChanges();
      } else {
        this.isAddMode = false;
        this.selectedDepartmentId = this.employeeDetailsViewModel.departmentId;
        this.selectedZoneId = this.employeeDetailsViewModel.zoneId;
        this.checkIsInPrimaryRole();
        this.populateRiskPermissionsIfEmpty();
        this.changeDetectorRef.detectChanges();
      }

      if (
        this.employeeDetailsViewModel.hubObjectId &&
        this.employeeDetailsViewModel.hubObjectId > 0 &&
        !(this.employee.hubObjectId && this.employee.hubObjectId > 0)
      ) {
        this.isAddMode = false;
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.promoteExternalUser) {
      this.selectPrimaryRole(EmployeeRoleTypes.General_User);
      this.changeDetectorRef.detectChanges();
    } else if (!this.employeeDetailsViewModel || !this.employeeDetailsViewModel.id) {
      this.selectPrimaryRole(EmployeeRoleTypes.General_User);
      this.changeDetectorRef.detectChanges();
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  updateEmployeeDepartment(filters: FilterViewModel[]) {
    this.filters = filters;
    const departmentFilter = filters.find((f) => f.filterType === FilterTypes.Department);
    this.employeeDetailsViewModel.departmentId = departmentFilter ? +departmentFilter.filterValue : 0;
    this.selectedDepartmentId = this.employeeDetailsViewModel.departmentId;
    // When removing a department we need to over all of the permissions to change Only_User_Department to All_departments
    if (filters.length === 0) {
      this.employeeDetailsViewModel.permissions.forEach((p) => {
        if (p.departmentVisibility === DepartmentVisibilities.Only_User_Department) {
          p.departmentVisibility = DepartmentVisibilities.All_Departments;
        }
      });
    }
  }

  updateEmployeeZone(filters: FilterViewModel[]) {
    this.filters = filters;
    const zoneFilter = filters.find((f) => f.filterType === FilterTypes.Zone);
    this.employeeDetailsViewModel.zoneId = zoneFilter ? +zoneFilter.filterValue : 0;
    this.selectedZoneId = this.employeeDetailsViewModel.zoneId;
  }

  /**
   * Primary roles means -- Account Admin | User Admin | Admin | General User
   * They have different behaviour than the other roles
   */
  selectPrimaryRole(role: EmployeeRoleTypes) {
    this.employeeDetailsViewModel.employeeRole = [];
    this.employeeDetailsViewModel.permissions = this.employeeDetailsViewModel.permissions ? this.employeeDetailsViewModel.permissions.filter(p => p.value !== undefined && p.value > 0 && p.type !== FilterTypes.Department) : [];
    this.employeeDetailsViewModel.employeeRole.push({
      employeeId: this.employeeDetailsViewModel.id,
      id: 0,
      roleId: role,
      created: '',
      accountId: 0,
    });
    // Clear all selected filter to clear the dropdown component
    if (role === EmployeeRoleTypes.General_User) {
      if (this.account.usePlanning) {
        this.planningComponent.selectPlanningUserRole(true);
      }
      if (this.account.useRisk) {
        this.riskComponent.selectedRiskManager(true);
      }
      if (this.account.useIMS || this.account.useIMSOnly) {
        this.controlComponent.selectIncidentUserRole(true);
      }
      if (this.account.useRunSheets) {
        this.runsheetsComponent.selectRunsheetsUserRole(true);
      }
      if (this.account.useSustainability) {
        this.sustainabilityComponent.selectSusUserRole(true);
      }

      // Must change that when with similar approach to planning one.. when other modules are done
      this.employeeDetailsViewModel.modulePermissionViewModels.push(
        {
          employeeId: this.employeeDetailsViewModel.id,
          id: 0,
          module: ModuleTypes.Runsheets,
          objectType: 0,
          createdById: this.employee.id,
          created: '',
          accountId: this.employee.accountId,
        }
      );
    }
    this.checkIsInPrimaryRole();
  }

  generatePermission(
    module: ModuleTypes,
    filterType: FilterTypes,
    value: number,
    departmentVisibility: DepartmentVisibilities,
    readOrWrite: ReadWriteTypes,
    confidential: boolean,
    level: number = 0
  ) {
    const obj: PermissionViewModel = {
      module: module,
      type: filterType,
      value: value,
      departmentVisibility: departmentVisibility,
      read: readOrWrite === ReadWriteTypes.read || readOrWrite === ReadWriteTypes.write,
      write: readOrWrite === ReadWriteTypes.write,
      confidential: confidential,
      level: level,
      employeeId: this.employeeDetailsViewModel.id,
      accountId: +this.account.id,
      id: 0,
      createdById: this.employee.id
    };
    return obj;
  }

  isInRole(role: EmployeeRoleTypes): boolean {
    return this.employeeDetailsViewModel.employeeRole.some((r) => r.roleId === role);
  }


  /**
   * validates if the user have access to operate with the role
   */
  canAccessRole(accountRole:EmployeeRoleTypes){

  const isUserAccountAdmin = this.employee.employeeRole.some((r) => r.roleId === EmployeeRoleTypes.Account_Admin);
  const isUserAdmin = this.employee.employeeRole.some((r) => r.roleId === EmployeeRoleTypes.User_Admin);
  const isAdmin =  this.employee.employeeRole.some((r) => r.roleId === EmployeeRoleTypes.Admin);

  const editUserIsAccountAdmin = this.employeeDetailsViewModel.employeeRole.some((r) => r.roleId === EmployeeRoleTypes.Account_Admin);
  const editUserIsUserAdmin = this.employeeDetailsViewModel.employeeRole.some((r) => r.roleId === EmployeeRoleTypes.User_Admin);


  if(this.employee.id === this.employeeDetailsViewModel.id){
    return isUserAccountAdmin;
  }
  else{
    switch(accountRole){
      case EmployeeRoleTypes.Account_Admin:
        return isUserAccountAdmin;
      case EmployeeRoleTypes.User_Admin:
        return (isUserAccountAdmin || isUserAdmin);
      case EmployeeRoleTypes.Admin:
        return (!editUserIsAccountAdmin || !editUserIsUserAdmin ) && (isUserAccountAdmin || isUserAdmin || isAdmin);
      case EmployeeRoleTypes.General_User:
        return true;
      default:
        return false;
    }
  }
  }

  checkIsInPrimaryRole() {
    this.isAccountAdmin = this.isInRole(EmployeeRoleTypes.Account_Admin);
    this.isInPrimaryRole = EmployeeUtil.IsAdmin(this.employeeDetailsViewModel);
  }

  populatePrivacyPermissionsIfEmpty() {
    // If user is Account admin that means he can access all privacy projects
    if (this.isAccountAdmin) {
      this.employeeDetailsViewModel.permissions.push(
        this.generatePermission(
          ModuleTypes.Planning,
          FilterTypes.Project,
          0,
          DepartmentVisibilities.All_Departments,
          ReadWriteTypes.write,
          true
        )
      );
      this.employeeDetailsViewModel.permissions.push(
        this.generatePermission(
          ModuleTypes.Incidents,
          FilterTypes.Incident,
          0,
          DepartmentVisibilities.All_Departments,
          ReadWriteTypes.write,
          true,
          1
        )
      );
      this.employeeDetailsViewModel.permissions.push(
        this.generatePermission(
          ModuleTypes.Incidents,
          FilterTypes.Incident,
          0,
          DepartmentVisibilities.All_Departments,
          ReadWriteTypes.write,
          true,
          2
        )
      );
      this.employeeDetailsViewModel.permissions.push(
        this.generatePermission(
          ModuleTypes.Incidents,
          FilterTypes.Incident,
          0,
          DepartmentVisibilities.All_Departments,
          ReadWriteTypes.write,
          true,
          3
        )
      );
      this.employeeDetailsViewModel.permissions.push(
        this.generatePermission(
          ModuleTypes.Risk,
          FilterTypes.Risk,
          0,
          DepartmentVisibilities.All_Departments,
          ReadWriteTypes.write,
          true
        )
      );
    } else {
      const findedProjectsPrivacyPermission = this.employeeDetailsViewModel.permissions.some(
        (p) => p.module === ModuleTypes.Planning && p.type === FilterTypes.Project && p.confidential === true
      );

      if (!findedProjectsPrivacyPermission) {
        this.employeeDetailsViewModel.permissions.push(
          this.generatePermission(
            ModuleTypes.Planning,
            FilterTypes.Project,
            -1,
            DepartmentVisibilities.All_Departments,
            ReadWriteTypes.write,
            true
          )
        );
      }
    }
  }

  populatePlanningPermissionsIfEmpty() {
    // If user is in primary roles dont add empty projects
    if (this.isInPrimaryRole) {
      return;
    }
    const foundReadOnlyPermission = this.employeeDetailsViewModel.permissions.some(
      (p) => p.module === ModuleTypes.Planning && p.type === FilterTypes.Project && p.read && !p.write && p.confidential === false
    );
    const foundWritePermission = this.employeeDetailsViewModel.permissions.some(
      (p) => p.module === ModuleTypes.Planning && p.type === FilterTypes.Project && p.write && p.confidential === false
    );

    // If there is no read permission generate one with -1 value
    if (!foundReadOnlyPermission) {
      this.employeeDetailsViewModel.permissions.push(
        this.generatePermission(
          ModuleTypes.Planning,
          FilterTypes.Project,
          -1,
          DepartmentVisibilities.All_Departments,
          ReadWriteTypes.read,
          false
        )
      );
    }
    // If there is no write permission generate one with -1 value
    if (!foundWritePermission) {
      const generatedWriteEdgeCasePermission = {
        module: ModuleTypes.Planning,
        type: FilterTypes.Project,
        value: -1,
        departmentVisibility: DepartmentVisibilities.All_Departments,
        read: false,
        write: true,
        confidential: false,
        level: 0,
        employeeId: this.employeeDetailsViewModel.id,
        accountId: +this.account.id,
        id: 0,
        createdById: this.employee.id
      };
      this.employeeDetailsViewModel.permissions.push(generatedWriteEdgeCasePermission);
    }
  }

  populateRiskPermissionsIfEmpty() {
    // If user is in primary roles dont add risk permissions
    if (this.isInPrimaryRole) {
      return;
    }
    const foundReadOnlyPermission = this.employeeDetailsViewModel.permissions.some(
      (p) => p.module === ModuleTypes.Risk && p.type === FilterTypes.Risk && p.read && !p.write && p.confidential === false
    );
    const foundWritePermission = this.employeeDetailsViewModel.permissions.some(
      (p) => p.module === ModuleTypes.Risk && p.type === FilterTypes.Risk && p.write && p.confidential === false
    );

    // If there is no read permission generate one with -1 value
    if (!foundReadOnlyPermission) {
      this.employeeDetailsViewModel.permissions.push(
        this.generatePermission(
          ModuleTypes.Risk,
          FilterTypes.Risk,
          -1,
          DepartmentVisibilities.All_Departments,
          ReadWriteTypes.read,
          false
        )
      );
    }
    // If there is no write permission generate one with -1 value
    if (!foundWritePermission) {
      const generatedWriteEdgeCasePermission = {
        module: ModuleTypes.Risk,
        type: FilterTypes.Risk,
        value: -1,
        departmentVisibility: DepartmentVisibilities.All_Departments,
        read: false,
        write: true,
        confidential: false,
        level: 0,
        employeeId: this.employeeDetailsViewModel.id,
        accountId: +this.account.id,
        id: 0,
        createdById: this.employee.id
      };
      this.employeeDetailsViewModel.permissions.push(generatedWriteEdgeCasePermission);
    }
  }

  onClose() {
    this.bsModalRef.hide();
  }

  deleteClicked() {
    this.confirmationService.confirmThis(
      `Deleting a ${this.localisedEmployee} is permanent and will remove them from all items assigned to them. You could make them inactive instead.`,
      () => {
        this.employeeSettingsService.deleteEmployees([this.employeeDetailsViewModel.id]).subscribe(() => {
          void this.alertService.success(`${this.localisationService.localise('Employee')} updated successfully.`);
          this.allowedFiltersService.refreshFilters(
            OperationTypes.Delete,
            [{ id: this.employeeDetailsViewModel.id, title: this.employeeDetailsViewModel.salutation, relatedObjectId: 0 }],
            FilterTypes.Employee
          );
          this.onDelete.next(this.employeeDetailsViewModel);
          this.bsModalRef.hide();
        });
      },
      () => {
        //handle cancel
      },
      'Permanent delete',
      true
    );
  }

  saveClicked() {
    this.saveButtonLoader = true;
    this.employeeDetailsViewModel.accountId = this.authenticationService.getCurrentAccount().id;
    this.populatePrivacyPermissionsIfEmpty();
    this.populatePlanningPermissionsIfEmpty();
    this.populateRiskPermissionsIfEmpty();

    this.employeeDetailsViewModel = { ...this.employeeDetailsViewModel, ...this.form.getRawValue() } as EmployeeDetailsViewModel;

    if (this.isAddMode) {
      if(this.isHubAccount){
        this.employeeDetailsViewModel.employeeRole.push({
          employeeId: this.employeeDetailsViewModel.id,
          id: 0,
          roleId: EmployeeRoleTypes.Hub_General,
          created: '',
          accountId: 0,
        });
      }

      this.addEmployee();
    } else {
      this.updateEmployee();
    }
  }

  saveAndSendInvite() {

    this.employeeDetailsViewModel.accountId = this.authenticationService.getCurrentAccount().id;
    this.saveButtonLoader = true;
    this.employeeDetailsViewModel.isActive = true;
    this.employeeDetailsViewModel.sendNotification = true;

    if(this.isHubAccount){

      this.employeeDetailsViewModel.employeeRole.push({
        employeeId: this.employeeDetailsViewModel.id,
        id: 0,
        roleId: EmployeeRoleTypes.Hub_General,
        created: '',
        accountId: 0,
      });

    }

    this.saveClicked();

  }

  addEmployee() {
    this.subscriptions.add(
      this.employeeSettingsService.addEmployee(this.employeeDetailsViewModel).subscribe((model) => {
        this.saveButtonLoader = false;
        if (!model.errorList || !model.errorList.length) {
          const employee = model.returnModel as Employee;
          const employeeId = employee.id;

          if (this.employeeDetailsViewModel.notificationSettings) {
            this.employeeDetailsViewModel.notificationSettings.forEach((notificationModel) => {
              notificationModel.employeeId = employeeId;
              this.employeeSettingsService
                .updateEmployeeSettingInUserArea(employeeId, notificationModel.title, notificationModel)
                .subscribe();
            });
          }

          if (this.employeeUserGroups && this.employeeUserGroups.length) {
            const ids: number[] = [];
            this.employeeUserGroups.forEach((g) => ids.push(g.id));
            this.userGroupService.addUserToGroups(employeeId, ids).subscribe();
          }
          void this.alertService.success(`${this.localisationService.localise('Employee')} added successfully`);
          this.allowedFiltersService.refreshFilters(
            OperationTypes.Create,
            [
              {
                id: employeeId,
                title: this.employeeDetailsViewModel.firstName + ' ' + this.employeeDetailsViewModel.surname,
                relatedObjectId: 0,
              },
            ],
            FilterTypes.Employee
          );
          this.onSave.next(this.employeeDetailsViewModel);
          this.bsModalRef.hide();
        } else {
          void this.alertService.error(model.errorList[0]);
        }
      })
    );
  }

  updateEmployee() {
    this.employeeSettingsService.updateEmployeeFullDetails(this.employeeDetailsViewModel).subscribe(() => {
      this.saveButtonLoader = false;
      if (this.employeeDetailsViewModel.id === this.employee.id) {
        const zoneId = this.employeeDetailsViewModel.zoneId ? this.employeeDetailsViewModel.zoneId.toString() : '0';
        this.fixedZoneFiltersService.setFixedFilter(
          this.fixedZoneFiltersService.allowedFilters.find((f) => f.filterValue.toString() === zoneId)
        );
      }

      this.employeeSettingsService.updateEmployeePermissions(this.employeeDetailsViewModel).subscribe(() => {
        if (this.employeeDetailsViewModel.notificationSettings) {
          this.employeeDetailsViewModel.notificationSettings.forEach((notificationModel) => {
            notificationModel.employeeId = this.employeeDetailsViewModel.id;
            this.employeeSettingsService
              .updateEmployeeSettingInUserArea(this.employeeDetailsViewModel.id, notificationModel.title, notificationModel)
              .subscribe();
          });
        }

        const employeeFilterObject = {
          id: this.employeeDetailsViewModel.id,
          title: `${this.employeeDetailsViewModel.firstName} ${this.employeeDetailsViewModel.surname}`,
          relatedObjectId: 0,
        };

        // Handle promotion from external user to regular employee
        if (this.promoteExternalUser) {
          // Remove from External_User filter
          this.allowedFiltersService.refreshFilters(
            OperationTypes.Delete,
            [employeeFilterObject],
            FilterTypes.External_User
          );
          // Add to Employee filter (which also clones to Owner and other types)
          this.allowedFiltersService.refreshFilters(
            OperationTypes.Create,
            [employeeFilterObject],
            FilterTypes.Employee
          );
        } else {
          this.allowedFiltersService.refreshFilters(
            OperationTypes.Update,
            [employeeFilterObject],
            FilterTypes.Employee,
            true
          );
        }

        void this.alertService.success(`${this.localisationService.localise('Employee')} updated successfully.`);
        this.onSave.next(this.employeeDetailsViewModel);
        this.bsModalRef.hide();
      });
    });
  }

  setActiveUser(isAdd: boolean) {
    this.employeeDetailsViewModel.isActive = isAdd;
  }

  showConfirmUserIdp(externalAvailable) {
    this.externalIsAvailable = externalAvailable;
    this.isConfirmUserIdpVisible = true;
  }

  setOverrideAuthType(hasIdp: boolean | null | undefined) {
    if (hasIdp == null) {
      this.employeeDetailsViewModel.employeeHasIdp = !this.employeeDetailsViewModel.employeeHasIdp;
    } else {
      this.employeeDetailsViewModel.employeeHasIdp = hasIdp;
    }
  }

  hideConfirmUserIdp() {
    this.isConfirmUserIdpVisible = false;
  }

  handleConfirmUserContinueClick() {
    this.hideConfirmUserIdp()
    this.saveAndSendInvite();
  }

  handleSaveAndSendClick(send : boolean) {
    if (!this.form.value.username) return;
    this.saveButtonLoader = true;
    var accountGuid = this.authenticationService.getCurrentAccount().accountUID;
    const sources = {
      ecvm: this.idpService.getIdpConfirmation(this.form.value.username),
      //Refresh account config in case an admin value has changed eg hasIdp or allowedDomains
      config: this.authenticationManager.getConfiguration(accountGuid)
    }
    forkJoin(sources).subscribe(({ ecvm, config }) => {
      this.account = this.authenticationService.getCurrentAccount();
      this.saveButtonLoader = false;
      this.employeeConfirmViewModel = ecvm;
      const userDomain = this.extractDomain(this.employeeConfirmViewModel.email);
      const accountHasIdp = this.account.hasIdp;
      const accountAllowedDomains = this.account.allowedDomains;
      //User domain is allowed if its included in the account allowed domains or if account allowed domains is empty
      const userDomainIsAllowed = !accountAllowedDomains || accountAllowedDomains.length === 0 || accountAllowedDomains.includes(userDomain);
      const externalIsAvailable = accountHasIdp && userDomainIsAllowed;
      //Set the defalult value for the employee has idp
      if (!this.employeeConfirmViewModel.userExistsInIdp) {
        //Where the user does not exist set it to the account default
        this.employeeDetailsViewModel.employeeHasIdp = externalIsAvailable && this.employeeConfirmViewModel.userDomainIsFederated;
      } else {
        //Where the user does exist, if the user is external then set to true, otherwise set it based on the values in the validation.
        const userIsConfirmedInternal = !this.employeeConfirmViewModel.exisitngUserIsExternal && this.employeeConfirmViewModel.existingUserRedeemed;
        this.employeeDetailsViewModel.employeeHasIdp = this.employeeConfirmViewModel.exisitngUserIsExternal || (!userIsConfirmedInternal && externalIsAvailable);
      }
      if (send) {
        if ((!externalIsAvailable) && !this.employeeConfirmViewModel.userExistsInIdp) {
          //the account does not have an external identity provider or domain is not allowed and the user is not yet registered in our IdP then no need to show a confirmation.  We are setting up a new internal user.
          if (!this.employeeDetailsViewModel.isActive) {
            this.confirmationService.confirmThis(
              this.translateService.instant(T.settings.employees_departments_org.auth_messages.inactive_users_will_become_active),
              () => { this.saveAndSendInvite(); },
              () => { } //handle cancel
            );
          } else {
            this.saveAndSendInvite();
          }
        } else {
          //Otherwise show a confirmation.        
          this.showConfirmUserIdp(externalIsAvailable);
          this.changeDetectorRef.detectChanges();
        }
      } else {
        this.saveClicked();
      }      
    });    
  }

  /**
    * Extracts the domain from a valid email address.
    * @param email - The email address string.
    * @returns The domain part of the email, or null if invalid.
    */
  extractDomain(email: string): string | null {
    if (typeof email !== "string") return null;

    // Basic email validation and domain extraction
    const match = email.match(/^[^\s@]+@([^\s@]+\.[^\s@]+)$/);
    return match ? match[1] : null;
  }

  /**
   * This code here is for debugging purposes
   */

  testLogInConsole() {
    // ROLES
    const copiedRoles: EmployeeRole[] = JSON.parse(JSON.stringify(this.employeeDetailsViewModel.employeeRole)) as EmployeeRole[];
    const rolesObj = {};
    copiedRoles.forEach((r, i) => {
      rolesObj[i] = { ...r };
      rolesObj[i]['roleId'] = EmployeeRoleTypes[r.roleId];
    });

    //PERMISSIONS
    const copiedPermissions: PermissionViewModel[] = JSON.parse(JSON.stringify(this.employeeDetailsViewModel.permissions)) as PermissionViewModel[];
    const permissionsObj = {};
    copiedPermissions.forEach((p, i) => {
      permissionsObj[i] = { ...p };
      permissionsObj[i]['departmentVisibility'] = DepartmentVisibilities[p.departmentVisibility];
      permissionsObj[i]['module'] = ModuleTypes[p.module];
      permissionsObj[i]['type'] = FilterTypes[p.type];
    });

  }

  selectVisibleTabsed() {
    if (this.account.usePlanning) {
      this.selectedModuleTab = this.moduleTabsIds[0];
    } else if (this.account.useIMS || this.account.useIMSOnly) {
      this.selectedModuleTab = this.moduleTabsIds[1];
    } else if (this.account.useRunSheets) {
      this.selectedModuleTab = this.moduleTabsIds[2];
    }
  }

  changeEmployeeGroups(groups: UserGroupViewModel[]) {
    this.employeeUserGroups = groups;
  }

  private initForm(): void {
    this.form = this.fb.group<EmployeeFormControls>({
      firstName: this.fb.control<string>(this.employeeDetailsViewModel.firstName ?? '', {
        nonNullable: true,
        validators: [
          Validators.required,
          this.wtValidators.textInput(1, 80, WtValidators.RESTRICTED_CHARS.DEFAULT)
        ]
      }),
      surname: this.fb.control<string>(this.employeeDetailsViewModel.surname ?? '', {
        nonNullable: true,
        validators: [
          Validators.required,
          this.wtValidators.textInput(1, 80, WtValidators.RESTRICTED_CHARS.DEFAULT)
        ]
      }),
      username: this.fb.control<string>(this.employeeDetailsViewModel.username ?? '', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.email,
          Validators.maxLength(200)
        ]
      }),
    });
  }
}
